import { forwardRef, useImperativeHandle, useRef, useState, ReactElement } from 'react';
import { Block, Box, useTranslation } from '@common';
import * as Base from '@base';
import styles from '../style.module.less';
import type { CellSetting } from './grid-designer';
import type { QuestionnaireModalForm, QuestionnaireModalRef } from './types';

/**
 * 用于修改表格列宽的属性
 * 自动宽度时 width 为空，否则取值为 '150px' | '30%'
 *
 * 以下代码拷贝自旧 web-sdk 工程的 src/form/widget/modal.js
 */
function QuestionnaireModalComponent(props: unknown, ref: QuestionnaireModalRef): ReactElement {
  const [t] = useTranslation();
  const [show, setShow] = useState<boolean>(false);

  // 保持对话框关闭回调的引用
  const confirm = useRef<(value: CellSetting) => void>(() => null);

  // 创建表单实例
  const [form] = Base.Form.useForm<QuestionnaireModalForm>();
  const autoWidth = Base.Form.useWatch('autoWidth', form);

  async function onShow(option: CellSetting): Promise<CellSetting> {
    const width = option.width ?? '';
    form.setFieldsValue({
      width,
      autoWidth: width.length === 0 ? 'auto' : 'custom'
    });
    setShow(true);
    return await new Promise(resolve => confirm.current = resolve);
  }

  async function handleOk(): Promise<void> {
    const { width, autoWidth } = await form.validateFields();
    confirm.current({
      width: autoWidth === 'auto' ? '' : width,
      title: '',
      field: ''
    });
    setShow(false);
  }

  /**
   * 自定义校验列宽值
   */
  async function validateWidth(rule: unknown, value: string): Promise<void> {
    if (value.length === 0) {
      return;
    }
    if (!/^\d+(px|%)$/.test(value)) {
      return await Promise.reject(t('请输入正确的列宽'));
    }
  }

  useImperativeHandle(ref, () => ({ show: onShow }));

  return (
    <Base.Modal
      title={t('设置')}
      open={show}
      onOk={handleOk}
      onCancel={() => setShow(false)}
      okText={t('确定')}
      okButtonProps={{ autoInsertSpace: false }}
      cancelButtonProps={{ autoInsertSpace: false }}
      cancelText={t('取消')}>
      <Box>
        <Base.Form
          form={form}
          initialValues={{ width: '', autoWidth: 'auto', index: -1 }}
          layout='vertical'>
          <Base.Form.Item name='autoWidth' label={t('列宽')}>
            <Base.Radio.Group buttonStyle='solid' className={styles.antRadioButtonCustom}>
              <Base.Radio.Button value='auto'>{t('自动')}</Base.Radio.Button>
              <Base.Radio.Button value='custom'>{t('选填')}</Base.Radio.Button>
            </Base.Radio.Group>
          </Base.Form.Item>
          <Block if={autoWidth !== 'auto'}>
            <Base.Form.Item
              name='width'
              label={t('宽度（例 : 150px | 30%)')}
              rules={[{ validator: validateWidth }]}>
              <Base.Input placeholder={t('请输入列宽')} />
            </Base.Form.Item>
          </Block>
        </Base.Form>
      </Box>
    </Base.Modal>
  );
}

export const QuestionnaireModal = forwardRef(QuestionnaireModalComponent);
